// @flow

import { isEmpty } from 'lodash';

import * as routeBuilder from 'Routes/routeBuilder';
import { QUERY_QUERY_PARAM } from 'Utils/faceting/constants';
import {
  formatDepartureDateForStore,
  getDepartureDateDisplayValue,
  serializeKidsToQueryParam,
} from 'Utils/faceting/facetingUtils';
import {
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';
import redirectUtils from 'Utils/uri/redirectUtils';
import { convertLocationToURI } from 'Utils/uri/uriUtils';

export const FACET_QUERY_PARAMS = [
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_QUERY_PARAM,
  QUERY_TRANSPORT,
];

export type SearchCriteria = {
  departureCity: string,
  departureDate: ?number,
  duration: ?number,
  keywords: string,
  kids: Array<*>,
  kidsValidationState: Array<ValidationState>,
  query: SearchQuery,
};

const computeQueryParamsForUrl = (args: {
  criteriaKeysToPersist: Array<string>,
  searchCriteria: SearchCriteria,
}) => {
  const { criteriaKeysToPersist, searchCriteria } = args;

  const formattedDate =
    searchCriteria.departureDate &&
    getDepartureDateDisplayValue(formatDepartureDateForStore(searchCriteria.departureDate, ''));
  const searchQuery = searchCriteria.query ? searchCriteria.query.label : '';

  return {
    [QUERY_DEPARTURE_DATE]:
      (criteriaKeysToPersist.includes(QUERY_DEPARTURE_DATE) && formattedDate) || undefined,
    [QUERY_DURATION]:
      (criteriaKeysToPersist.includes(QUERY_DURATION) && searchCriteria.duration) || undefined,
    [QUERY_TRANSPORT]:
      (criteriaKeysToPersist.includes(QUERY_TRANSPORT) && searchCriteria.departureCity) ||
      undefined,
    [QUERY_QUERY_PARAM]:
      (criteriaKeysToPersist.includes(QUERY_QUERY_PARAM) &&
        (searchCriteria.keywords || searchQuery)) ||
      undefined,
    [QUERY_KIDS]:
      (criteriaKeysToPersist.includes(QUERY_KIDS) &&
        searchCriteria.kids.length > 0 &&
        serializeKidsToQueryParam({
          kids: searchCriteria.kids,
          kidsValidationState: searchCriteria.kidsValidationState,
        })) ||
      undefined,
  };
};

export const navigateToSearchPage = (args: {
  criteriaKeysToPersist: Array<string>,
  location: SearchRouterLocation,
  searchCriteria: SearchCriteria,
  searchPageRouteName?: string,
}) => {
  const { criteriaKeysToPersist, location, searchCriteria, searchPageRouteName } = args;
  const queryParams = computeQueryParamsForUrl({
    criteriaKeysToPersist,
    searchCriteria,
  });

  // NOTE: URI.setQuery() does not remove existing queries
  const searchPageUrl = convertLocationToURI(location).setQuery(queryParams);
  const previousPathname = searchPageUrl.pathname();
  const pathname = searchPageRouteName ? routeBuilder.urlPattern(searchPageRouteName) : null;

  if (pathname) {
    searchPageUrl.pathname(pathname);
  }

  const isShallow = searchPageUrl.pathname() === previousPathname;

  redirectUtils.redirectTo()(searchPageUrl, { shallow: isShallow });
};

export const navigateToResortPage = (args: {
  location: SearchRouterLocation,
  query: SearchQuery,
  searchCriteria: SearchCriteria,
}) => {
  const { location, query, searchCriteria } = args;
  const queryParams = computeQueryParamsForUrl({
    criteriaKeysToPersist: [QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_TRANSPORT],
    searchCriteria,
  });

  const resortPageUrl = convertLocationToURI(location)
    .pathname(query.link)
    .setQuery(queryParams);

  redirectUtils.redirectTo()(resortPageUrl);
};

export const currentUrlContainsSearchQueryParam = (location: SearchRouterLocation): boolean => {
  return !isEmpty(location.queryParams[QUERY_QUERY_PARAM]);
};

export const currentUrlHasNoQueryParams = (location: SearchRouterLocation): boolean => {
  return FACET_QUERY_PARAMS.every(queryKey => isEmpty(location.queryParams[queryKey]));
};

export const getFacetsAsString = (
  filters: Array<{ id: string, name: string }>,
  facetCategoryId: string,
): string | null => {
  const facetsInCategory = filters.filter(filter => filter.id === facetCategoryId);
  if (facetsInCategory.length === 0) {
    return null;
  }

  return facetsInCategory.map(filter => filter.name).join('|');
};
